import { makeAutoObservable, runInAction } from "mobx";
import i18n from "i18next";
import dayjs, { Dayjs } from "dayjs";
import MainStore from "MainStore";
import { validate, validateField } from "./valid";
import { getOutgoingDocument } from "api/OutgoingDocument";
import { createOutgoingDocument } from "api/OutgoingDocument";
import { updateOutgoingDocument } from "api/OutgoingDocument";

// dictionaries
import { getDocumentTypes } from "api/DocumentType";
import { getIncomingDocumentsByApprovalRequest } from "api/IncomingDocument";
import { getuser_accounts } from "api/user_accounts";

import storeList from "../OutgoingDocumentListView/store"
import { getDocumentPrefixByApprovalRequest } from "api/Organisation_setting";


class NewStore {
  id = 0
  approval_request_id = 0
  incoming_document_id = 0
  document_type_id = 0
  name = ""
  file_name = ""
  file_path = ""
  file_size = 0
  mime_type = ""
  file_hash = ""
  document_number = ""
  document_date: Dayjs | null = dayjs()
  version = 1
  prepared_by = 0
  prepared_at: Dayjs | null = null
  signed_by = 0
  signed_at: Dayjs | null = null
  is_final = false
  sent_to_bga = false
  sent_at: Dayjs | null = null
  
  // File upload fields
  File = null
  idDocumentInputKey = ""

  errors: { [key: string]: string } = {};

  // Автонумерация
  docNumberPrefix = ""
  docNextNumber = ""
  docNumberManual = false  // флаг ручного ввода

  // Справочники
  document_types = []
  incoming_documents = []
  users = []


  constructor() {
    makeAutoObservable(this);
    this.idDocumentInputKey = Math.random().toString(36);
  }

  clearStore() {
    runInAction(() => {
      this.id = 0
      this.approval_request_id = 0
      this.incoming_document_id = 0
      this.document_type_id = 0
      this.name = ""
      this.file_name = ""
      this.file_path = ""
      this.file_size = 0
      this.mime_type = ""
      this.file_hash = ""
      this.document_number = ""
      this.document_date = dayjs()
      this.version = 1
      this.prepared_by = 0
      this.prepared_at = null
      this.signed_by = 0
      this.signed_at = null
      this.is_final = false
      this.sent_to_bga = false
      this.sent_at = null
      
      // Clear file fields
      this.File = null
      this.idDocumentInputKey = Math.random().toString(36)
      this.docNumberPrefix = ""
      this.docNextNumber = ""
      this.docNumberManual = false
      this.errors = {}
    });
  }

  handleChange(event) {
    const { name, value } = event.target;
    (this as any)[name] = value;
    this.validateField(name, value);
  }

  async validateField(name: string, value: any) {
    const { isValid, error } = await validateField(name, value);
    if (isValid) {
      this.errors[name] = "";
    } else {
      this.errors[name] = error;
    }
  }

  changeDocInputKey() {
    this.idDocumentInputKey = Math.random().toString(36);
  }

  async onSaveClick(onSaved: (id: number) => void) {
    var data = {
      id: this.id - 0,
      approval_request_id: storeList.idMain,
      incoming_document_id: this.incoming_document_id - 0 || null,
      document_type_id: this.document_type_id - 0,
      name: this.name,
      file_name: this.file_name,
      file_path: this.file_path,
      file_size: this.file_size - 0,
      mime_type: this.mime_type,
      file_hash: this.file_hash,
      document_number: this.document_number,
      document_date: this.document_date ? dayjs(this.document_date).toISOString() : null,
      version: this.version - 0,
      prepared_by: this.prepared_by - 0 || null,
      prepared_at: this.prepared_at ? dayjs(this.prepared_at).toISOString() : null,
      signed_by: this.signed_by - 0 || null,
      signed_at: this.signed_at ? dayjs(this.signed_at).toISOString() : null,
      is_final: this.is_final,
      sent_to_bga: this.sent_to_bga,
      sent_at: this.sent_at ? dayjs(this.sent_at).toISOString() : null,
    };

    const { isValid, errors } = await validate(data);
    if (!isValid) {
      this.errors = errors;
      MainStore.openErrorDialog(i18n.t("message:error.alertMessageAlert"));
      return;
    }

    try {
      MainStore.changeLoader(true);
      let response;
      if (this.id === 0) {
        response = await createOutgoingDocument(data, this.file_name, this.File);
      } else {
        response = await updateOutgoingDocument(data, this.file_name, this.File);
      }
      if (response.status === 201 || response.status === 200) {
        onSaved(response);
        if (data.id === 0) {
          MainStore.setSnackbar(i18n.t("message:snackbar.successSave"), "success");
        } else {
          MainStore.setSnackbar(i18n.t("message:snackbar.successEdit"), "success");
        }
        // Clear form after successful save
        this.clearStore();
      } else {
        throw new Error();
      }
    } catch (err) {
      MainStore.setSnackbar(i18n.t("message:somethingWentWrong"), "error");
    } finally {
      MainStore.changeLoader(false);
    }
  };

  async doLoad(id: number) {
    // загрузка справочников
    await this.loadDocumentTypes();
    await this.loadIncomingDocuments();
    await this.loadUsers();
    this.id = id;

    if (id === null || id === 0) {
      this.clearStore();
      // Загружаем шифр нумерации для нового документа
      if (storeList.idMain) {
        this.loadDocumentPrefix();
      }
      return;
    }

    this.loadOutgoingDocument(id);
  }

  toggleDocNumberManual = () => {
    runInAction(() => {
      this.docNumberManual = !this.docNumberManual;
      if (!this.docNumberManual) {
        // Возврат к автономеру
        this.document_number = this.docNextNumber;
      }
    });
  };

  loadDocumentPrefix = async () => {
    try {
      const response = await getDocumentPrefixByApprovalRequest(storeList.idMain) as any;
      if (response?.data?.prefix) {
        runInAction(() => {
          this.docNumberPrefix = response.data.prefix;
          this.docNextNumber = response.data.next_number ?? "";
          // Автоматически подставляем следующий номер в поле
          if (!this.docNumberManual) {
            this.document_number = this.docNextNumber;
          }
        });
      }
    } catch {
      // не критично
    }
  };

  loadDocumentTypes = async () => {
    try {
      MainStore.changeLoader(true);
      const response = await getDocumentTypes();
      if ((response.status === 201 || response.status === 200) && response?.data !== null) {
        runInAction(() => {
          this.document_types = response.data
        });
      } else {
        throw new Error();
      }
    } catch (err) {
      MainStore.setSnackbar(i18n.t("message:somethingWentWrong"), "error");
    } finally {
      MainStore.changeLoader(false);
    }
  }

  loadIncomingDocuments = async () => {
    try {
      MainStore.changeLoader(true);
      const response = await getIncomingDocumentsByApprovalRequest(storeList.idMain);
      if ((response.status === 201 || response.status === 200) && response?.data !== null) {
        runInAction(() => {
          this.incoming_documents = response.data
        });
      } else {
        throw new Error();
      }
    } catch (err) {
      MainStore.setSnackbar(i18n.t("message:somethingWentWrong"), "error");
    } finally {
      MainStore.changeLoader(false);
    }
  }

  loadUsers = async () => {
    try {
      MainStore.changeLoader(true);
      const response = await getuser_accounts();
      if ((response.status === 201 || response.status === 200) && response?.data !== null) {
        runInAction(() => {
          this.users = response.data
        });
      } else {
        throw new Error();
      }
    } catch (err) {
      MainStore.setSnackbar(i18n.t("message:somethingWentWrong"), "error");
    } finally {
      MainStore.changeLoader(false);
    }
  }

  loadOutgoingDocument = async (id: number) => {
    try {
      MainStore.changeLoader(true);
      const response = await getOutgoingDocument(id);
      if ((response.status === 201 || response.status === 200) && response?.data !== null) {
        runInAction(() => {
          this.id = response.data.id;
          this.approval_request_id = response.data.approval_request_id;
          this.incoming_document_id = response.data.incoming_document_id;
          this.document_type_id = response.data.document_type_id;
          this.name = response.data.name;
          this.file_name = response.data.file_name || "";
          this.file_path = response.data.file_path;
          this.file_size = response.data.file_size;
          this.mime_type = response.data.mime_type;
          this.file_hash = response.data.file_hash;
          this.document_number = response.data.document_number;
          this.document_date = response.data.document_date ? dayjs(response.data.document_date) : null;
          this.version = response.data.version;
          this.prepared_by = response.data.prepared_by;
          this.prepared_at = response.data.prepared_at ? dayjs(response.data.prepared_at) : null;
          this.signed_by = response.data.signed_by;
          this.signed_at = response.data.signed_at ? dayjs(response.data.signed_at) : null;
          this.is_final = response.data.is_final;
          this.sent_to_bga = response.data.sent_to_bga;
          this.sent_at = response.data.sent_at ? dayjs(response.data.sent_at) : null;
        });
      } else {
        throw new Error();
      }
    } catch (err) {
      MainStore.setSnackbar(i18n.t("message:somethingWentWrong"), "error");
    } finally {
      MainStore.changeLoader(false);
    }
  };
}

export default new NewStore();